import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from './Badge';
import { Switch } from './Switch';
import { SectionCard } from './SectionCard';
import { Database, Lock } from 'lucide-react';

/**
 * Renders a database schema (tables, views, relationships) and — when the user
 * can manage the selection — lets them mark each table active/inactive inline.
 *
 * Inactive tables are excluded from every downstream stage (schema analysis,
 * data profiling, semantic mapping, agent creation, query/endpoint generation).
 * The selection must be configured before the first analysis and locks afterwards.
 *
 * Props:
 *   schemaData – { tables:[{ name, columns?, is_active }], views, relationships, database_type }
 *   canManage  – boolean: show the active/inactive toggles + save controls
 *   locked     – boolean: selection frozen (database already analyzed)
 *   saving     – boolean: parent-controlled save in flight
 *   onSave     – (payload:[{ table_name, is_active }]) => Promise
 */
export const SchemaViewer = ({
    schemaData,
    canManage = false,
    locked = false,
    saving = false,
    onSave,
}) => {
    const { t } = useTranslation(['ui-complex', 'common']);
    const tables = useMemo(
        () => (schemaData?.tables && Array.isArray(schemaData.tables) ? schemaData.tables : []),
        [schemaData]
    );

    // Persisted active/inactive state from the backend (is_active flag per table).
    const persisted = useMemo(() => {
        const map = {};
        tables.forEach((t) => {
            map[t.name] = t.is_active !== false;
        });
        return map;
    }, [tables]);

    // Local, editable copy used while toggling before a save.
    const [selection, setSelection] = useState({});
    useEffect(() => {
        setSelection(persisted);
    }, [persisted]);

    const activeCount = useMemo(
        () => tables.filter((t) => selection[t.name] ?? true).length,
        [tables, selection]
    );

    // Auto-save on every toggle: optimistic update, persist the full selection,
    // and revert if the backend rejects it (e.g. the last active table).
    const handleToggle = async (name, value) => {
        if (locked || saving || !onSave) return;
        const previous = selection;
        const next = { ...selection, [name]: value };
        setSelection(next);
        try {
            await onSave(tables.map((t) => ({ table_name: t.name, is_active: next[t.name] ?? true })));
        } catch (e) {
            setSelection(previous);
        }
    };

    // Bulk enable/disable every table.
    // Enable all → persist immediately. Disable all → clear LOCALLY only (the backend
    // requires ≥1 active, so an all-off save would be rejected); the cleared state is
    // saved as soon as the user enables at least one table (each toggle sends the full
    // map). This lets a user pick a few of many tables without toggling every one off.
    const handleSetAll = async (value) => {
        if (locked || saving || !onSave) return;
        const next = {};
        tables.forEach((t) => {
            next[t.name] = value;
        });
        if (!value) {
            setSelection(next);
            return;
        }
        const previous = selection;
        setSelection(next);
        try {
            await onSave(tables.map((t) => ({ table_name: t.name, is_active: true })));
        } catch (e) {
            setSelection(previous);
        }
    };

    if (!schemaData || Object.keys(schemaData).length === 0) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <p className="text-gray-500">{t('ui-complex:schemaViewer.noSchemaData')}</p>
            </div>
        );
    }

    const { views = [], relationships = [], database_type } = schemaData;

    return (
        <div className="space-y-6">
            {/* Database Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Database className="w-5 h-5 text-primary-600" />
                    {t('ui-complex:schemaViewer.databaseInformation')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">{t('ui-complex:schemaViewer.databaseType')}</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">{database_type || t('common:unknown')}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">{canManage ? t('ui-complex:schemaViewer.activeTables') : t('ui-complex:schemaViewer.tablesCount')}</p>
                        <p className="text-sm font-medium text-gray-900">
                            {canManage ? `${activeCount} / ${tables.length}` : tables?.length || 0}
                        </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">{t('ui-complex:schemaViewer.viewsCount')}</p>
                        <p className="text-sm font-medium text-gray-900">{views?.length || 0}</p>
                    </div>
                </div>
            </div>

            {/* Tables Section */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="text-xl font-semibold text-gray-900">{t('ui-complex:schemaViewer.tablesHeading', { count: tables?.length || 0 })}</h3>

                    {/* Inline auto-save status */}
                    {canManage && tables.length > 0 && (
                        locked ? (
                            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                                <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                                {t('ui-complex:schemaViewer.selectionLocked')}
                            </div>
                        ) : (
                            <span className="text-xs text-gray-500">
                                {saving ? (
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                                        {t('common:saving')}
                                    </span>
                                ) : (
                                    t('ui-complex:schemaViewer.changesSavedAutomatically')
                                )}
                            </span>
                        )
                    )}
                </div>

                {canManage && tables.length > 0 && !locked && (
                    <p className="text-sm text-gray-500">
                        {t('ui-complex:schemaViewer.toggleHint')}
                    </p>
                )}

                {canManage && tables.length > 0 && !locked && (
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
                        <Switch
                            checked={activeCount === tables.length}
                            disabled={saving}
                            onChange={(value) => handleSetAll(value)}
                        />
                        <span className="text-sm font-medium text-gray-800">
                            {activeCount === tables.length ? t('ui-complex:schemaViewer.allTablesEnabled') : t('ui-complex:schemaViewer.enableAllTables')}
                        </span>
                        {activeCount === 0 ? (
                            <span className="ml-auto text-xs text-amber-600">{t('ui-complex:schemaViewer.enableAtLeastOne')}</span>
                        ) : (
                            <span className="ml-auto text-xs text-gray-400">{t('ui-complex:schemaViewer.activeOfTotal', { active: activeCount, total: tables.length })}</span>
                        )}
                    </div>
                )}

                {tables && tables.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {tables.map((table, index) => {
                          const isActive = selection[table.name] ?? (table.is_active !== false);
                          const isInactive = !isActive;
                          return (
                            <SectionCard
                                key={table.name || index}
                                defaultOpen={false}
                                className={isInactive ? 'opacity-60' : ''}
                                title={
                                    <span className={`font-semibold flex items-center gap-2 min-w-0 ${isInactive ? 'text-gray-400' : 'text-gray-900'}`}>
                                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${isInactive ? 'bg-gray-200 text-gray-400' : 'bg-primary-100 text-primary-700'}`}>
                                            🗂️
                                        </span>
                                        <span className={`truncate ${isInactive ? 'line-through' : ''}`}>{table.name}</span>
                                    </span>
                                }
                                actions={
                                    <>
                                        {isInactive && (
                                            <Badge variant="warning" className="text-xs">
                                                {t('common:inactive')}
                                            </Badge>
                                        )}
                                        <Badge variant="secondary" className="text-xs">
                                            {t('ui-complex:schemaViewer.colsCount', { count: table.columns?.length || 0 })}
                                        </Badge>
                                        {canManage && (
                                            <Switch
                                                checked={isActive}
                                                disabled={locked || saving}
                                                onChange={(value) => handleToggle(table.name, value)}
                                            />
                                        )}
                                    </>
                                }
                            >
                                <h5 className="text-sm font-medium text-gray-700 mb-3">{t('ui-complex:schemaViewer.columns')}</h5>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {table.columns && table.columns.length > 0 ? (
                                        table.columns.map((column, colIndex) => (
                                            <div key={colIndex} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                                        {column.name}
                                                    </span>
                                                    <Badge variant="outline" className="text-xs capitalize">
                                                        {column.type}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!column.nullable && (
                                                        <Badge variant="destructive_subtle" className="text-xs">
                                                            {t('ui-complex:schemaViewer.notNull')}
                                                        </Badge>
                                                    )}
                                                    {column.default !== null && column.default !== undefined && (
                                                        <Badge variant="outline" className="text-xs">
                                                            {t('ui-complex:schemaViewer.defaultValue', { value: String(column.default) })}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">{t('ui-complex:schemaViewer.noColumnsFound')}</p>
                                    )}
                                </div>

                                {table.foreign_keys && table.foreign_keys.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <h5 className="text-sm font-medium text-gray-700 mb-2">{t('ui-complex:schemaViewer.foreignKeys')}</h5>
                                        <div className="space-y-1">
                                            {table.foreign_keys.map((fk, fkIndex) => (
                                                <div key={fkIndex} className="text-sm text-gray-600 bg-yellow-50 p-2 rounded">
                                                    {fk.constrained_columns?.join(', ')} → {fk.referred_table}.{fk.referred_columns?.join(', ')}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {table.indexes && table.indexes.length > 0 && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <h5 className="text-sm font-medium text-gray-700 mb-2">{t('ui-complex:schemaViewer.indexes')}</h5>
                                        <div className="space-y-1">
                                            {table.indexes.map((idx, idxIndex) => (
                                                <div key={idxIndex} className="text-sm text-gray-600 bg-green-50 p-2 rounded">
                                                    {idx.name}: {idx.column_names?.join(', ')}
                                                    {idx.unique && <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">{t('ui-complex:schemaViewer.unique')}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </SectionCard>
                          );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <p className="text-gray-500">No tables found in the database.</p>
                    </div>
                )}
            </div>

            {/* Views Section */}
            {views && views.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Views ({views.length})</h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {views.map((view, index) => (
                            <SectionCard
                                key={view.name || index}
                                defaultOpen={false}
                                title={
                                    <span className="font-semibold text-gray-900 flex items-center gap-2 min-w-0">
                                        <span className="bg-green-100 text-green-700 w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0">
                                            👁️
                                        </span>
                                        <span className="truncate">{view.name}</span>
                                    </span>
                                }
                                actions={
                                    <Badge variant="secondary" className="text-xs">
                                        {view.columns?.length || 0} cols
                                    </Badge>
                                }
                            >
                                <h5 className="text-sm font-medium text-gray-700 mb-3">{t('ui-complex:schemaViewer.columns')}</h5>
                                <div className="space-y-2">
                                    {view.columns && view.columns.length > 0 ? (
                                        view.columns.map((column, colIndex) => (
                                            <div key={colIndex} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                                                    {column.name}
                                                </span>
                                                <Badge variant="outline" className="text-xs capitalize">
                                                    {column.type}
                                                </Badge>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">{t('ui-complex:schemaViewer.noColumnsFound')}</p>
                                    )}
                                </div>
                            </SectionCard>
                        ))}
                    </div>
                </div>
            )}

            {/* Relationships Section */}
            {relationships && relationships.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-gray-900">Relationships ({relationships.length})</h3>

                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="space-y-3">
                            {relationships.map((relationship, index) => (
                                <div key={index} className="flex items-center gap-2 p-3 bg-primary-50 rounded-lg border border-primary-100">
                                    <span className="text-primary-600 font-medium">{relationship.from_table}.{relationship.from_column}</span>
                                    <span className="text-gray-500">→</span>
                                    <span className="text-primary-600 font-medium">{relationship.to_table}.{relationship.to_column}</span>
                                    <span className="ml-auto text-xs text-gray-500 bg-white px-2 py-1 rounded">
                                        {relationship.type || 'FK'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchemaViewer;
